import {
  Sparkles,
  TrendingUp,
  Target,
  ShieldCheck,
  UserCog,
  Clock,
} from "lucide-react";

/**
 * "What Leadership Needs Now" — a presentation-only summary card shown
 * at the top of the Leadership persona view.
 *
 * Uses ONLY existing CRE-generated data from the incident object. If a
 * field is missing we render "Not available in current analysis." — we
 * never invent values.
 */
const SEVERITY_TO_IMPACT = {
  P1: { label: "High", tone: "rose" },
  P2: { label: "Elevated", tone: "amber" },
  P3: { label: "Moderate", tone: "sky" },
  P4: { label: "Low", tone: "emerald" },
};

const TONE_CLASSES = {
  rose: "border-rose-200 bg-rose-50 text-rose-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  sky: "border-sky-200 bg-sky-50 text-sky-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
};

function decisionStatus(status) {
  const map = {
    pending_analysis: { label: "Awaiting analysis", tone: "slate" },
    analyzing: { label: "Analysing", tone: "sky" },
    awaiting_approval: { label: "Approval required", tone: "amber" },
    approved: { label: "Approved", tone: "emerald" },
    rejected: { label: "Rejected", tone: "rose" },
    modified: { label: "Modified", tone: "violet" },
  };
  return map[status] || { label: status || "Unknown", tone: "slate" };
}

function bandFromScore(score) {
  if (score == null) return null;
  if (score >= 9.0) return { label: "Excellent", tone: "emerald" };
  if (score >= 7.5) return { label: "Strong", tone: "emerald" };
  if (score >= 6.0) return { label: "Moderate", tone: "amber" };
  if (score >= 4.0) return { label: "Weak", tone: "rose" };
  return { label: "Poor", tone: "rose" };
}

function truncate(s, n = 90) {
  if (!s) return null;
  const t = String(s).trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

function MetricCard({ icon: Icon, label, value, tone = "slate", footer }) {
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.slate;
  return (
    <div className="cre-surface rounded-sm p-3.5 md:p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="cre-overline text-slate-500">{label}</div>
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md border ${toneCls}`}>
          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
      </div>
      <div className="text-sm md:text-[15px] font-semibold text-slate-900 leading-snug">
        {value || (
          <span className="text-slate-400 font-normal">
            Not available in current analysis.
          </span>
        )}
      </div>
      {footer ? (
        <div className={`inline-flex w-fit px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${toneCls}`}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export default function LeadershipSummary({ incident }) {
  const impact = SEVERITY_TO_IMPACT[incident?.severity] || null;
  const triageImpact =
    incident?.triage?.summary ||
    incident?.executive_summary?.business_impact ||
    incident?.affected_system ||
    null;

  const rootCauseSummary =
    incident?.root_cause_analysis?.failure_mechanism ||
    incident?.root_cause_analysis?.symptom ||
    null;

  const score = incident?.cre_score?.overall_score ?? null;
  const scoreBand = bandFromScore(score);
  const scorePct =
    score != null ? `${Math.round((score / 10) * 100)}%` : null;

  const decision = decisionStatus(incident?.status);

  const auditCount = Array.isArray(incident?.audit_trail)
    ? incident.audit_trail.length
    : 0;

  return (
    <div
      data-testid="leadership-summary"
      className="cre-surface rounded-sm p-4 md:p-5 border-violet-200 bg-gradient-to-br from-white to-violet-50/30"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-600" strokeWidth={1.75} />
        <h2 className="font-heading text-base md:text-lg font-bold tracking-tight text-slate-900">
          What Leadership Needs Now
        </h2>
      </div>
      <p className="mt-1 text-[12px] text-slate-500">
        Curated from the existing CRE analysis. Switch persona to see
        role-specific detail.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={TrendingUp}
          label="Business Impact"
          value={truncate(triageImpact, 110)}
          tone={impact ? impact.tone : "slate"}
          footer={impact ? impact.label : null}
        />
        <MetricCard
          icon={Target}
          label="Root Cause Summary"
          value={truncate(rootCauseSummary, 110)}
          tone="violet"
          footer={
            incident?.root_cause_analysis ? "From RCA synthesizer" : null
          }
        />
        <MetricCard
          icon={ShieldCheck}
          label="CRE Confidence"
          value={scorePct ? `${scorePct} — ${scoreBand?.label || ""}` : null}
          tone={scoreBand?.tone || "slate"}
          footer={scoreBand?.label || null}
        />
        <MetricCard
          icon={UserCog}
          label="Decision Needed / Approval"
          value={decision.label}
          tone={decision.tone}
          footer={
            incident?.approval?.decision
              ? `Last decision · ${incident.approval.decision}`
              : null
          }
        />
      </div>

      <div className="mt-3 flex items-center gap-2 text-[11.5px] text-slate-500">
        <Clock className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span>
          Timeline · {auditCount}{" "}
          {auditCount === 1 ? "audit event" : "audit events"} captured
        </span>
      </div>
    </div>
  );
}
